import { useState, useEffect, ReactNode, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRefreshTokenByURLMutation } from 'api/oauth/oauth';
import { selectIsAuthenticated } from 'redux/user/user.selectors';
import { userActions } from 'redux/user/user.slice';
import CenteredProgress from 'components/progress/centered-progress/centered-progress.component';
import { merchantActions } from 'redux/app/merchant/merchant.slice';

type RefreshGateProps = {
  children: ReactNode;
};

const RefreshTokenValidationGate = ({ children }: RefreshGateProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const authData = useMemo(() => queryParams.get('authData'), [queryParams]);
  const [isRefreshTokenValidating, setRefreshTokenValidating] = useState(
    !!authData,
  );
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [refreshTokenByURLMutation] = useRefreshTokenByURLMutation();

  useEffect(() => {
    if (!authData) return;

    const setUrl = () => {
      queryParams.delete('authData');
      navigate({ search: `?${queryParams.toString()}` });
    };

    const refreshTokenRequest = async ({
      refresh_token,
      merchant_id,
      client_id,
      account_id,
    }) => {
      try {
        const data = await refreshTokenByURLMutation({
          refresh_token,
          grant_type: 'refresh_token',
          scope: 'read write',
          client_id,
        }).unwrap();
        dispatch(userActions.refreshToken(data));
        dispatch(
          userActions.setAccountIdAndClientId({ client_id, account_id }),
        );
      } catch (_e) {
      } finally {
        setUrl();
        dispatch(merchantActions.setCurrent(merchant_id));
        setRefreshTokenValidating(false);
      }
    };

    try {
      const decodedAuthData = JSON.parse(window.atob(authData));

      if (!isAuthenticated) {
        refreshTokenRequest(decodedAuthData);
      } else {
        setUrl();
        dispatch(merchantActions.setCurrent(decodedAuthData.merchant_id));
        setRefreshTokenValidating(false);
      }
    } catch (_e) {
      setUrl();
      setRefreshTokenValidating(false);
    }
  }, [
    authData,
    dispatch,
    isAuthenticated,
    navigate,
    queryParams,
    refreshTokenByURLMutation,
  ]);

  if (isRefreshTokenValidating)
    return <CenteredProgress sx={{ position: 'absolute' }} />;

  return children;
};

export default RefreshTokenValidationGate;
